import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { TemplateCategoryApiService } from '../services/template-category-api.service';
import { TemplateCategoryDrawerService } from '../services/template-category-drawer.service';
import { TemplateCategory } from '../models/template.model';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';


@Component({
  selector: 'app-template-category-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent],
  templateUrl: './template-category-modal.component.html',
})
export class TemplateCategoryModalComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();

  isDrawerOpen = false;
  items: TemplateCategory[] = [];
  filtered: TemplateCategory[] = [];
  paginated: TemplateCategory[] = [];

  loading = false;
  error = '';
  filtroSearch = '';

  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: TemplateCategoryApiService,
    private drawerService: TemplateCategoryDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
    this.drawerService.state
      .pipe(takeUntil(this.destroy$))
      .subscribe((s) => (this.isDrawerOpen = s.isOpen));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.items = data ?? [];
          this.aplicarFiltros();
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error cargando categorías';
          this.loading = false;
        },
      });
  }

  private aplicarFiltros(): void {
    const term = this.filtroSearch.trim().toLowerCase();
    let data = [...this.items];
    if (term) {
      data = data.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          (c.description ?? '').toLowerCase().includes(term)
      );
    }
    this.filtered = data;
    this.globalTotalElements = data.length;
    this.globalTotalPages = Math.max(1, Math.ceil(data.length / this.pageSize));
    if (this.globalPage >= this.globalTotalPages) this.globalPage = 0;
    const start = this.globalPage * this.pageSize;
    this.paginated = data.slice(start, start + this.pageSize);
  }

  buscar(): void {
    this.globalPage = 0;
    this.aplicarFiltros();
  }

  onPageSizeChange(): void {
    this.globalPage = 0;
    this.aplicarFiltros();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.aplicarFiltros();
  }

  nuevo(): void {
    this.drawerService.open();
  }

  editar(item: TemplateCategory): void {
    this.drawerService.open({ categoryId: item.idCategory });
  }

  async eliminar(item: TemplateCategory): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar categoría',
      message: `¿Eliminar "${item.name}"? Las plantillas que la usen quedarán sin categoría.`,
    });
    if (!ok) return;
    this.api
      .delete(item.idCategory)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.deleted('Categoría');
          this.cargar();
        },
        error: (err) =>
          this.confirmDialog.alert({
            title: 'No se pudo eliminar',
            message: err.error?.message || 'Error al eliminar la categoría',
            variant: 'danger',
          }),
      });
  }

  onOverlayClick(): void {
    if (this.isDrawerOpen) return;
    this.cerrar();
  }

  cerrar(): void {
    this.closed.emit();
  }

  trackById = (_: number, c: TemplateCategory) => c.idCategory;
}
